import { Button } from "@frontend/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@frontend/components/ui/card"
import { DataTable } from "@frontend/components/ui/data-table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { useApiQuery } from "@frontend/hooks/use-api"
import {
  useAddProjectMember,
  useProject,
  useProjectMemberCandidates,
  useProjectMembers,
  useRemoveProjectMember,
  useUpdateProjectMemberRole
} from "@frontend/hooks/use-projects"
import { formatProjectRole } from "@frontend/pages/project-form/lib/utils"
import type { ColumnDef } from "@tanstack/react-table"
import { ArrowLeft, Plus, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import { Link, useParams } from "react-router"

interface Member {
  id: string
  role: string
  created_at: string
  user_id: string
  email: string
  name: string
}

type ProjectRole = "project_admin" | "annotator" | "reviewer" | "viewer"

export interface ProjectMembersViewProps {
  projectId: string
  projectName?: string
  isLoading: boolean
  isAdmin: boolean
  showAddForm: boolean
  selectedUserId: string
  selectedRole: ProjectRole
  availableMembers: Member[]
  projectMembers: Member[]
  addError?: string
  isAdding: boolean
  onOpenAddForm: () => void
  onCloseAddForm: () => void
  onSelectUser: (userId: string) => void
  onSelectRole: (role: ProjectRole) => void
  onAddMember: () => void
  onRoleChange: (memberId: string, role: ProjectRole) => void
  onRemoveMember: (memberId: string) => void
}

export function ProjectMembersView({
  projectId,
  projectName,
  isLoading,
  isAdmin,
  showAddForm,
  selectedUserId,
  selectedRole,
  availableMembers,
  projectMembers,
  addError,
  isAdding,
  onOpenAddForm,
  onCloseAddForm,
  onSelectUser,
  onSelectRole,
  onAddMember,
  onRoleChange,
  onRemoveMember
}: ProjectMembersViewProps) {
  const columns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span className="text-sm font-medium">{row.original.name}</span>
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.email}</span>
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: ({ row }) =>
          isAdmin ? (
            <Select
              value={row.original.role}
              onValueChange={(value) => onRoleChange(row.original.id, value as ProjectRole)}
            >
              <SelectTrigger className="w-40" size="sm">
                <SelectValue>{formatProjectRole(row.original.role)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="project_admin">Project Admin</SelectItem>
                <SelectItem value="annotator">Annotator</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <span className="capitalize">{row.original.role.replace("_", " ")}</span>
          )
      },
      ...(isAdmin
        ? [
            {
              id: "actions",
              accessorFn: (row) => row.id,
              header: "Actions",
              cell: ({ row }) => (
                <div className="text-right">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove member"
                    onClick={() => onRemoveMember(row.original.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              )
            } as ColumnDef<Member>
          ]
        : [])
    ],
    [isAdmin, onRemoveMember, onRoleChange]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Loading members...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/projects/${projectId}`}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Project Members</h1>
            <p className="text-muted-foreground text-sm">{projectName}</p>
          </div>
        </div>
        {isAdmin && availableMembers.length > 0 ? (
          <Button size="sm" onClick={onOpenAddForm}>
            <Plus className="h-4 w-4 mr-1" />
            Add Member
          </Button>
        ) : null}
      </div>

      {showAddForm ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Add Member</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1">
                <label htmlFor="project-member-user" className="text-xs text-muted-foreground">
                  Instance Member
                </label>
                <Select value={selectedUserId} onValueChange={(value) => onSelectUser(value ?? "")}>
                  <SelectTrigger id="project-member-user">
                    <SelectValue placeholder="Select member">
                      {availableMembers.find((m) => m.user_id === selectedUserId)
                        ? `${availableMembers.find((m) => m.user_id === selectedUserId)?.name} (${availableMembers.find((m) => m.user_id === selectedUserId)?.email})`
                        : undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                    {availableMembers.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.name} ({m.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label htmlFor="project-member-role" className="text-xs text-muted-foreground">
                  Role
                </label>
                <Select value={selectedRole} onValueChange={(v) => onSelectRole(v as ProjectRole)}>
                  <SelectTrigger id="project-member-role" className="w-40">
                    <SelectValue>{formatProjectRole(selectedRole)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_admin">Project Admin</SelectItem>
                    <SelectItem value="annotator">Annotator</SelectItem>
                    <SelectItem value="reviewer">Reviewer</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={onAddMember} disabled={!selectedUserId || isAdding}>
                {isAdding ? "Adding..." : "Add"}
              </Button>
              <Button size="sm" variant="outline" onClick={onCloseAddForm}>
                Cancel
              </Button>
            </div>
            {addError ? <p className="text-xs text-destructive mt-2">{addError}</p> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto p-3">
            <DataTable
              columns={columns}
              data={projectMembers}
              emptyMessage="No project members found."
              showPagination={false}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function ProjectMembersContainer() {
  const { projectId } = useParams<{ projectId: string }>()
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState("")
  const [selectedRole, setSelectedRole] = useState<ProjectRole>("annotator")

  const safeProjectId = projectId ?? ""

  const { data: project } = useProject(safeProjectId)
  const { data: currentUser } = useApiQuery<{
    id: string
    instance_role: "admin" | "member" | null
  }>(["auth", "me"], "/auth/me")
  const { data: membersResult, isLoading } = useProjectMembers(safeProjectId)
  const { data: memberCandidatesResult } = useProjectMemberCandidates(safeProjectId)

  const addMember = useAddProjectMember(safeProjectId)
  const updateRole = useUpdateProjectMemberRole(safeProjectId)
  const removeMember = useRemoveProjectMember(safeProjectId)

  const projectMembers: Member[] = membersResult?.data ?? []
  const availableMembers: Member[] = memberCandidatesResult?.data ?? []
  const currentUserId = currentUser?.id

  const isInstanceAdmin = currentUser?.instance_role === "admin"
  const isProjectAdmin = projectMembers.some((m) => m.user_id === currentUserId && m.role === "project_admin")
  const isAdmin = isInstanceAdmin || isProjectAdmin

  const handleAddMember = () => {
    if (!selectedUserId) return
    addMember.mutate(
      { user_id: selectedUserId, role: selectedRole },
      {
        onSuccess: () => {
          setShowAddForm(false)
          setSelectedUserId("")
          setSelectedRole("annotator")
        }
      }
    )
  }

  const handleRoleChange = (memberId: string, role: ProjectRole) => {
    updateRole.mutate({ memberId, role })
  }

  const handleRemove = (memberId: string) => {
    if (confirm("Remove this member from the project?")) {
      removeMember.mutate(memberId)
    }
  }

  return (
    <ProjectMembersView
      projectId={safeProjectId}
      projectName={project?.name}
      isLoading={isLoading}
      isAdmin={isAdmin}
      showAddForm={showAddForm}
      selectedUserId={selectedUserId}
      selectedRole={selectedRole}
      availableMembers={availableMembers}
      projectMembers={projectMembers}
      addError={addMember.isError ? addMember.error.message : undefined}
      isAdding={addMember.isPending}
      onOpenAddForm={() => setShowAddForm(true)}
      onCloseAddForm={() => setShowAddForm(false)}
      onSelectUser={setSelectedUserId}
      onSelectRole={setSelectedRole}
      onAddMember={handleAddMember}
      onRoleChange={handleRoleChange}
      onRemoveMember={handleRemove}
    />
  )
}

export const ProjectMembers = ProjectMembersContainer
